import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Phone } from "lucide-react";
import { BRAND } from "../../lib/brand";

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section id="home" className="relative pt-28 pb-20 sm:pt-32 sm:pb-28 overflow-hidden" data-testid="hero-section">
      {/* Decorative blobs */}
      <div className="blob-bg w-[420px] h-[420px] bg-brand-yellow/40 rounded-full -top-24 -left-24" />
      <div className="blob-bg w-[520px] h-[520px] bg-brand-blue/15 rounded-full -bottom-32 -right-20" />
      <div className="absolute inset-0 dotted-pattern opacity-20" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-float-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-soft-orange text-[color:var(--text-primary)] text-sm font-semibold" data-testid="hero-badge">
            <Sparkles size={16} className="text-brand-blue" />
            {BRAND.tagline}
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05] text-[color:var(--text-primary)]">
            Where little hearts grow into
            <span className="text-brand-blue"> curious </span>
            and{" "}
            <span className="relative inline-block">
              <span className="relative z-10">confident</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-brand-yellow/70 rounded-full -z-0" />
            </span>{" "}
            learners.
          </h1>

          <p className="mt-6 text-base sm:text-lg text-[color:var(--text-secondary)] max-w-2xl leading-relaxed">
            Innovative Preschool is a Nepalese-concept early childhood program in Panga, Kirtipur.
            We nurture children's innate qualities through play, language, creativity, movement,
            and warm everyday care.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => document.getElementById("admissions")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-primary"
              data-testid="hero-apply-btn"
            >
              Apply Now <ArrowRight size={18} />
            </button>
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost"
              data-testid="hero-contact-btn"
            >
              <Phone size={16} /> Contact Us
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat n="8+" l="Years caring" />
            <Stat n="200+" l="Happy families" />
            <Stat n="1:8" l="Teacher ratio" />
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-float-up delay-200">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-brand-yellow/70" />
            <div className="absolute -bottom-6 -right-6 w-36 h-36 rounded-[40%] bg-brand-blue/15" />
            <img
              src={BRAND.heroBg}
              alt="Children at Innovative Preschool"
              className="relative w-full h-[460px] object-cover rounded-[2.5rem] shadow-[0_30px_60px_rgba(30,91,184,0.18)]"
            />
            <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-2xl px-5 py-4 shadow-[0_18px_50px_rgba(30,91,184,0.18)] flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-yellow flex items-center justify-center font-bold text-[color:var(--text-primary)]">★</div>
              <div>
                <div className="font-heading font-bold text-sm">Trusted by families since 2017</div>
                <div className="text-xs text-[color:var(--text-secondary)]">Panga, Kirtipur, Nepal</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ n, l }) => (
  <div data-testid={`hero-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="font-heading text-2xl sm:text-3xl font-bold text-brand-blue">{n}</div>
    <div className="text-xs text-[color:var(--text-secondary)] mt-1">{l}</div>
  </div>
);

export default Hero;
